import Controller from '@ember/controller';
import { action } from '@ember/object';
import type PublicTransition from '@ember/routing/transition';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getReturnUrlFromTransition, redirect } from 'webapp/utils/common';

import PreviewUrlManager from 'webapp/models-custom/preview-url-manager';
import type Report from 'webapp/models-custom/report';
import ReportPresentManager from 'webapp/models-custom/report-present-manager';
import ReportUIHelper from 'webapp/models-custom/report-ui-helper';

import type { TrackingOriginForPresent } from 'webapp/services/model-manager/reports';

export default class ReportsViewReportController extends Controller {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('popup') declare popupService: Service.Popup;

	declare model: Report;

	enteringTransition: PublicTransition | null = null;

	override queryParams = ['present', 'itemId']; // so transitionTo w/ queryParams don't strip them out

	// bunch of this is (shortened) repeat from controllers/datawall

	@tracked reportPresentManager: ReportPresentManager | null = null; // set only if present is active

	@tracked previewUrlManager: PreviewUrlManager | null = null;
	@tracked reportUIHelper: ReportUIHelper | null = null;

	@tracked currentSlideIndex = 0;

	get currentVisibleSlide() {
		return this.model?.sortedSlides[this.currentSlideIndex] || null;
	}

	get slideSwitchToggleIconName() {
		return this.reportUIHelper?.slideSwitchToggleIconName || 'ChevronRight';
	}

	get isPageless() {
		return this.model?.isPageless;
	}

	get reportPresentManagerInstance(): ReportPresentManager {
		return this.reportPresentManager!;
	}

	get showPresentHelper() {
		return this.reportPresentManager && !this.isPageless;
	}

	@action
	updateReportIdParam(id: string) {
		const previewUrlManager = this.previewUrlManager;
		if (previewUrlManager) {
			previewUrlManager.itemIdParam = id;
		}
	}

	@action
	editReport() {
		redirect('/reports/editor/' + this.model.id);
	}

	@action
	onSlideChange(index: number): void {
		void this.popupService.closeAll();
		this.currentSlideIndex = index;
		if (this.reportPresentManager) {
			this.reportPresentManager.currentSlideIndex = index;
		}
		this.updatePathFromCurrentVisibleItemId();
		this.onLiveReportSlideChange();
	}

	activate() {
		this.previewUrlManager = new PreviewUrlManager(`/reports/view/${this.model.id}`);

		this.reportUIHelper = new ReportUIHelper(this.mmReportsService, this.eventManagerService);
		this.reportUIHelper.checkViewMode();

		const slideIdParam = this.previewUrlManager.itemIdParam,
			sortedSlidesIds = this.model.sortedSlidesIds;

		if (slideIdParam) {
			let index = sortedSlidesIds.indexOf(+slideIdParam);

			// in case slide was deleted from report we show first slide and update url
			if (index < 0) {
				index = 0;
				this.previewUrlManager.itemIdParam = String(sortedSlidesIds[0]);
			}

			this.currentSlideIndex = index;
		} else if (sortedSlidesIds.length > 0) {
			this.updatePathFromCurrentVisibleItemId();
		}

		this.onLiveReportSlideChange();

		if (!this.currentVisibleSlide?.isTypeBoard) {
			// element is in DOM. Otherwise wait until isLoadingReportSlideData becomes false, loader is removed
			this.reportUIHelper?.scheduleUpdateSlideSize(); // after data.reportSlides is in store
		}

		// if we have enteringTransition, means we are opening this from other page
		// and mmReports.initializePresentMode will take care of opening present mode
		if (this.previewUrlManager.isPresentView && !this.enteringTransition) {
			this.startPresentWithNotes('reports');
		}
	}

	deactivate() {
		this.stopPresentModeAndStay(); // in case we went with e.g. backspace from present mode

		this.previewUrlManager = null;

		if (this.reportUIHelper) {
			this.reportUIHelper.destroy(); // to unbind resize event
			this.reportUIHelper = null;
		}

		this.enteringTransition = null;
	}

	private onLiveReportSlideChange() {
		const slide = this.currentVisibleSlide;
		if (slide) {
			schedule('afterRender', () => {
				// slide components rendered
				this.reportUIHelper?.setSlideDescriptionAvailable(!!slide.description.content);
			});
		}
	}

	private updatePathFromCurrentVisibleItemId(): void {
		const sortedSlidesIds = this.model.sortedSlidesIds,
			currentVisibleSlide = this.currentVisibleSlide;

		if (sortedSlidesIds.length > 1 && !!currentVisibleSlide?.id) {
			this.previewUrlManager!.itemIdParam = currentVisibleSlide.id;
		}
	}

	// ------ Present mode ------

	startPresent(origin: TrackingOriginForPresent) {
		// present mode, browser fullscreen
		this.initializePresentMode(origin, true);
	}

	startPresentWithNotes(origin: TrackingOriginForPresent) {
		// present mode, extra popup window
		this.initializePresentMode(origin);
	}

	@action
	initializePresentMode(origin: string, isFullscreen = false) {
		const report = this.model;
		if (!report) {
			return;
		}

		this.analyticsService.event(TrackingEvent.PRESENT_SLIDE, {
			type: isFullscreen ? 'present' : 'present_notes',
			origin: origin as TrackingOriginForPresent,
		});

		this.onSlideChange(0);

		this.previewUrlManager!.isPresentView = true;
		this.mmReportsService.triggerOuterSlideResize();

		this.reportPresentManager = new ReportPresentManager(
			report,
			this.onSlideChange,
			this.stopPresentMode,
			isFullscreen,
			this.navigationService.theme.id,
		);
	}

	@action
	stopPresentMode() {
		if (!this.previewUrlManager) {
			// assume already stopped
			return;
		}
		this.previewUrlManager.isPresentView = false;
		this.mmReportsService.triggerOuterSlideResize();
		this.reportPresentManager = null;

		const enteringTransition = this.enteringTransition;

		if (enteringTransition) {
			const exitUrl = getReturnUrlFromTransition(
				enteringTransition,
				this.navigationService.router,
			);
			this.navigationService.transitionToRoute(exitUrl || 'reports');
		}
	}

	@action
	stopPresentModeAndStay() {
		this.enteringTransition = null;
		if (document.fullscreenElement) {
			void document.exitFullscreen();
		}
		this.reportPresentManager?.handleExit();
	}
}
